import React from 'react';
import { ChevronLeft, User, Shield, Activity, Cpu, Database, Server, Trash2, Power, Globe, Terminal } from 'lucide-react';

const TeamMemberDetailsSection = ({
  member,
  parentAdmin,
  userResourceUsage,
  allDeployments,
  setActiveSection,
  handleUserDelete,
  handleToggleActive,
  formatBytesValue
}) => {
  if (!member) {
    return (
      <div className="sa-section fade-in">
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          No team member selected.
          <button className="sa-action-btn" style={{ margin: '1rem auto' }} onClick={() => setActiveSection('users')}>Back to Users</button>
        </div>
      </div>
    );
  }

  // Get specific resource usage for this member
  const usage = userResourceUsage?.find(u => u.username === member.username) || {};
  const activeAppsCount = usage.activeApps || 0;
  const cpuPercent = usage.cpuPercent?.toFixed(1) || '0.0';
  const memUsageBytes = usage.memUsageBytes || 0;
  
  // Find specific deployments for this member
  const memberDeployments = allDeployments?.filter(dep => dep.owner === member.username || dep.userId === member.id) || [];

  return (
    <div className="sa-section fade-in">
      <div className="sa-section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="sa-action-btn" style={{ margin: 0, color: 'var(--text-main)', borderColor: 'var(--border)' }} onClick={() => setActiveSection('users')}>
            <ChevronLeft size={20} />
          </button>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {member.username} 
              <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '12px', background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>Team Member</span>
            </h2>
            <p className="subtitle">Detailed view of sub-user profile, resource consumption, and deployments.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        
        {/* Profile Card */}
        <div className="sa-card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="sa-org-avatar" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', width: '56px', height: '56px', fontSize: '1.4rem' }}>
              {member.username.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--text-main)' }}>{member.username}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{member.email || 'No email'}</div>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}><Shield size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}/> Role</span>
              <span style={{ fontWeight: 600, textTransform: 'capitalize', color: 'var(--text-main)' }}>{member.role || 'Developer'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}><User size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}/> Parent Admin</span>
              <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{parentAdmin?.username || 'Unknown'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}><Activity size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}/> Status</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: member.isActive !== false ? '#22c55e' : '#ef4444' }}></div>
                <span style={{ fontWeight: 600, color: member.isActive !== false ? '#22c55e' : '#ef4444' }}>
                  {member.isActive !== false ? 'Active' : 'Suspended'}
                </span>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button 
                className="sa-action-btn" 
                style={{ flex: 1, color: member.isActive !== false ? '#f59e0b' : '#10b981', borderColor: member.isActive !== false ? '#f59e0b' : '#10b981' }}
                onClick={(e) => handleToggleActive(member, e)}
              >
                <Power size={14} style={{ marginRight: '0.4rem' }}/> 
                {member.isActive !== false ? 'Suspend' : 'Activate'}
              </button>
              <button 
                className="sa-action-btn sa-action-delete" 
                style={{ flex: 1 }}
                onClick={() => handleUserDelete(member)}
              >
                <Trash2 size={14} style={{ marginRight: '0.4rem' }}/> Delete
              </button>
            </div>
          </div>
        </div>

        {/* Resource Allocation Card */}
        <div className="sa-card" style={{ padding: '1.5rem', background: 'var(--surface-hover)' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Database size={18} color="var(--primary)" /> Resource Allocations
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ background: 'var(--bg-main)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Server size={14}/> Active Apps</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-main)' }}>{activeAppsCount} <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-muted)' }}>Apps</span></div>
            </div>
            <div style={{ background: 'var(--bg-main)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Server size={14}/> Total Deploys</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-main)' }}>{memberDeployments.length} <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-muted)' }}>Apps</span></div>
            </div>
            <div style={{ background: 'var(--bg-main)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Cpu size={14}/> CPU Usage</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-main)' }}>{cpuPercent}%</div>
            </div>
            <div style={{ background: 'var(--bg-main)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Database size={14}/> RAM Usage</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(memUsageBytes)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Deployments List */}
      <div className="sa-card" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Globe size={18} color="var(--primary)" /> Managed Deployments
        </h3>
        
        {memberDeployments.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-main)', borderRadius: '8px' }}>
            This team member has no active deployments.
          </div>
        ) : (
          <div className="sa-table-wrapper" style={{ margin: 0 }}>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>App Name</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Domain</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {memberDeployments.map(dep => (
                  <tr key={dep.appName}>
                    <td>
                      <div className="sa-cell-bold">{dep.appName}</div>
                      <div className="sa-cell-muted" style={{ fontSize: '0.75rem' }}>Port: {dep.internalPort || 'N/A'}</div>
                    </td>
                    <td><span className="sa-role-badge" style={{ background: 'var(--surface-hover)', color: 'var(--text-main)', border: '1px solid var(--border)' }}>{dep.category || 'Unknown'}</span></td>
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.2rem 0.6rem', borderRadius: '4px', background: dep.status === 'running' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: dep.status === 'running' ? '#10b981' : '#ef4444', fontSize: '0.75rem', fontWeight: 600 }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: dep.status === 'running' ? '#10b981' : '#ef4444' }}></div>
                        {dep.status === 'running' ? 'Running' : 'Stopped'}
                      </div>
                    </td>
                    <td className="sa-cell-muted">{dep.domain || 'N/A'}</td>
                    <td style={{ textAlign: 'right' }}>
                       <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Managed directly by member</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default TeamMemberDetailsSection;
